/**  @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Boundary } from '@floating-ui/react';
import classNames from 'classnames';

import { breakpoints } from '@coursera/cds-core';

import PauseSvg from 'bundles/video-player/assets/svg/PauseSvg';
import PlayArrowSvg from 'bundles/video-player/assets/svg/PlayArrowSvg';
import SVGWrapper from 'bundles/video-player/assets/svg/SVGWrapper';
import ModernTooltipWrapper from 'bundles/video-player/components/ModernTooltipWrapper';
import type { VideoPlayerEventingV3UIFunction } from 'bundles/video-player/types/VideoPlayer';

import _t from 'i18n!nls/video-player';

type Props = {
  player: any;
  videoBoundaryElement?: Boundary;
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction;
};

type State = {
  isPlaying: boolean;
};

const styles = {
  playToggleTooltipWrapper: css`
    ${breakpoints.down('xs')} {
      height: 30px;
      display: flex;
      align-items: center;
    }

    // extra scope needed right now to override vjs style
    .rc-PlayToggle {
      position: relative;
      text-align: center;
      margin-top: 0;
      margin-left: 1em;
      margin-right: 0;
      padding: 0;
      color: #dedede;
      height: 100%;
      width: 3em;
      flex: none;

      .cif-play {
        font-size: 1.6em;
      }

      .cif-pause {
        font-size: 1.6em;
      }
    }
  `,
};

class PlayToggle extends React.Component<Props, State> {
  state = {
    isPlaying: false,
  };

  componentDidMount() {
    const { player } = this.props;
    this.setState({
      isPlaying: !player.paused(),
    });
    player.on('play', this.onPlay);
    player.on('pause', this.onPause);
  }

  componentWillUnmount() {
    const { player } = this.props;
    player.off('play', this.onPlay);
    player.off('pause', this.onPause);
  }

  onPlay = () => {
    this.setState({ isPlaying: true });
  };

  onPause = () => {
    this.setState({ isPlaying: false });
  };

  togglePlaying = () => {
    const { isPlaying } = this.state;
    const { player, trackVideoPlayerClickEvent } = this.props;

    if (isPlaying) {
      player.pause();
      trackVideoPlayerClickEvent('video_pause', {});
    } else {
      player.play();
      trackVideoPlayerClickEvent('video_play', {});
    }
  };

  render() {
    const { isPlaying } = this.state;
    const { videoBoundaryElement } = this.props;
    const label = isPlaying ? _t('Pause') : _t('Play');
    const tooltip = label;

    // NOTE: continuing to use the VJS skin styles for now
    // once we get stuff into React we can migrate the skin
    return (
      <ModernTooltipWrapper
        css={styles.playToggleTooltipWrapper}
        className="play-toggle-tooltip-wrapper"
        placement="top"
        message={tooltip}
        videoBoundaryElement={videoBoundaryElement}
        keyboardShortcut="spacebar"
        offset={20}
      >
        <button
          type="button"
          onClick={this.togglePlaying}
          className={classNames('rc-PlayToggle')}
          aria-label={label}
          aria-disabled={false}
        >
          <React.Fragment>
            <SVGWrapper>
              {isPlaying ? <PauseSvg width={28} height={28} /> : <PlayArrowSvg width={36} height={36} />}
            </SVGWrapper>
          </React.Fragment>

          <span className="vjs-control-text"> {label} </span>
        </button>
      </ModernTooltipWrapper>
    );
  }
}

export default PlayToggle;
